import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "styled-components";
import SettingsIcon from "@mui/icons-material/Settings";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { Link as RouteLink } from "react-router-dom";

const SubtitleText = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const SubtitleTextLight = styled.span`
    font-size: 12px;
    font-weight: 300;
    max-width: 368px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const TitleText = styled.p`
    font-size: 14px;
    margin: 0;
`;

const DashboardButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    margin-top: 13px;
    z-index: 10;
`;

const AllConfigLink = styled(RouteLink)`
    font-size: 12px;
    font-weight: 300;
    line-height: 20px;
    font-style: normal;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "auto",
    height: "auto",
    bgcolor: "background.paper",
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
};

const OrgDeploymentsConfigModal = ({ open, handleClose, isDemo, repos, fetchAction }) => {
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [data, setData] = useState({});
    const [deploymentsSelected, setDeploymentsSelected] = useState(false);
    const [releasesSelected, setReleasesSelected] = useState(false);
    const [pipelinesSelected, setPipelinesSelected] = useState(false);
    const [webhookSelected, setWebhookSelected] = useState(false);
    const [repo, setRepo] = useState(undefined);
    const [allRepos, setAllRepos] = useState([]);
    const [configurationDataPerRepo, setConfigurationDataPerRepo] = useState({});
    const [initialConfigurationDataPerRepo, setInitialConfigurationDataPerRepo] = useState({});
    const [checkedTerms, setCheckedTerms] = useState(false);

    const handleChange = (event) => {
        setRepo(event.target.value);
        setDeploymentsSelected(false);
        setReleasesSelected(false);
        setPipelinesSelected(false);
        setWebhookSelected(false);
    };

    const getConfigurationSettings = async () => {
        let response;
        if (isDemo) {
            response = (
                await import(
                    "src/scenes/dashboard/work-analytics/deployments-config/deployments-config-mock-data"
                )
            ).default;
        } else {
            response = await request(`/api/data/org/deployment-configuration`, { method: "GET" });
        }
        setData(response);
    };

    const handleGetConfiguration = async () => {
        try {
            getConfigurationSettings();
        } catch (e) {
            setMessage(`Error retrieving deployment configuration. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    const setConfigurationSettings = async () => {
        const alteredRepos = Object.keys(configurationDataPerRepo).reduce((acc, configRepo) => {
            if (
                JSON.stringify(configurationDataPerRepo[configRepo]) !==
                JSON.stringify(initialConfigurationDataPerRepo[configRepo])
            ) {
                const { defaultConfig, ...config } = configurationDataPerRepo[configRepo];
                acc[configRepo] = config;
            }
            return acc;
        }, {});
        if (Object.keys(alteredRepos).length === 0) {
            return;
        }
        await request(`/api/data/org/deployment-configuration`, {
            method: "POST",
            body: JSON.stringify(alteredRepos),
        });
        setMessage(`Deployment configuration saved successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
        handleGetConfiguration();
        fetchAction.refetch();
        handleClose();
    };

    const handleSubmitConfiguration = async () => {
        try {
            setConfigurationSettings();
        } catch (e) {
            setMessage(`Error saving deployment configuration. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    React.useEffect(() => {
        handleGetConfiguration();
    }, []);

    React.useEffect(() => {
        setConfigurationDataPerRepo(data && data.perRepo);
    }, [data]);

    React.useEffect(() => {
        if (data && data.perRepo) {
            const outerKeys = Object.keys(data.perRepo);
            setAllRepos(outerKeys);
            if (!repo) {
                setRepo(allRepos[0]);
            }
            setInitialConfigurationDataPerRepo(JSON.parse(JSON.stringify(data.perRepo)));
        }
    }, [data]);

    React.useEffect(() => {
        if (!repo) {
            setDeploymentsSelected(false);
            setReleasesSelected(false);
            setPipelinesSelected(false);
            setWebhookSelected(false);
        } else {
            const obj = data && data.perRepo && data.perRepo[repo];
            if (!obj || (obj && !obj.length)) {
                setDeploymentsSelected(false);
                setReleasesSelected(false);
                setPipelinesSelected(false);
                setWebhookSelected(false);
            }
            const innerObjKeys = obj && Object.keys(obj);
            if (innerObjKeys) {
                if (innerObjKeys.includes("deployments")) setDeploymentsSelected(true);
                if (innerObjKeys.includes("releases")) setReleasesSelected(true);
                if (innerObjKeys.includes("pipelines")) setPipelinesSelected(true);
                if (innerObjKeys.includes("webhook")) setWebhookSelected(true);
            }
        }
    }, [data, repo]);

    React.useEffect(() => {
        if (webhookSelected) {
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    ...configurationDataPerRepo[repo],
                    webhook: true,
                },
            });
        }
        if (!webhookSelected) {
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    ...configurationDataPerRepo[repo],
                    webhook: false,
                },
            });
        }
    }, [webhookSelected]);

    // Handle delete and replace from object
    React.useEffect(() => {
        if (
            !deploymentsSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            configurationDataPerRepo[repo].deployments
        ) {
            delete configurationDataPerRepo[repo].deployments;
        } else if (
            deploymentsSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            !configurationDataPerRepo[repo].deployments
        ) {
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    ...configurationDataPerRepo[repo],
                    deployments: {
                        environment: null,
                    },
                },
            });
        }
        if (
            !releasesSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            configurationDataPerRepo[repo].releases
        ) {
            delete configurationDataPerRepo[repo].releases;
        } else if (
            releasesSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            !configurationDataPerRepo[repo].releases
        ) {
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    ...configurationDataPerRepo[repo],
                    releases: {
                        name: null,
                    },
                },
            });
        }
        if (
            !pipelinesSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            configurationDataPerRepo[repo].pipelines
        ) {
            delete configurationDataPerRepo[repo].pipelines;
        } else if (
            pipelinesSelected &&
            configurationDataPerRepo &&
            configurationDataPerRepo[repo] &&
            !configurationDataPerRepo[repo].pipelines
        ) {
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    ...configurationDataPerRepo[repo],
                    pipelines: {
                        branch: null,
                        workflow: null,
                    },
                },
            });
        }
    }, [deploymentsSelected, releasesSelected, pipelinesSelected]);

    const handleCheckboxChange = (event) => {
        if (event.target.value === "deployments") {
            setDeploymentsSelected(!deploymentsSelected);
        }
        if (event.target.value === "releases") {
            setReleasesSelected(!releasesSelected);
        }
        if (event.target.value === "pipelines") {
            setPipelinesSelected(!pipelinesSelected);
        }
        if (event.target.value === "webhook") {
            setWebhookSelected(!webhookSelected);
        }
    };

    const handleTermsChange = (event) => {
        setCheckedTerms(event.target.checked);
    };

    const handleReset = () => {
        if (repo) {
            setDeploymentsSelected(true);
            setReleasesSelected(false);
            setPipelinesSelected(false);
            setWebhookSelected(false);
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    deployments: {
                        environment: null,
                    },
                },
            });
        }
    };

    return (
        <div>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Stack>
                    <Box sx={style}>
                        <Stack
                            direction="row"
                            style={{ width: "100%" }}
                            justifyContent="space-between"
                        >
                            <Stack direction="column">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    style={{ opacity: "0.8" }}
                                >
                                    <SettingsIcon />
                                    <Typography id="modal-modal-title" variant="h6" component="h2">
                                        Deployments configuration
                                    </Typography>
                                </Stack>
                                <SubtitleText id="modal-modal-description" sx={{ mt: 2, mb: 3 }}>
                                    Edit the parameters of your deployments.
                                </SubtitleText>
                                <SubtitleTextLight id="modal-modal-description" sx={{ mt: 3 }}>
                                    *Input the name of the env/tag/release/branch/workflow, or use %
                                    to select all available.
                                </SubtitleTextLight>
                                <AllConfigLink to="/deployments-config-all">
                                    View all configuration settings here
                                </AllConfigLink>
                            </Stack>
                            <DashboardButton
                                variant="outlined"
                                color="secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleReset();
                                }}
                                startIcon={<RestartAltIcon />}
                                style={{
                                    textDecoration: "auto",
                                    textAlign: "center",
                                    paddingRight: "12px",
                                    height: "36px",
                                }}
                            >
                                Reset Configuration
                            </DashboardButton>
                        </Stack>
                        <Stack
                            direction="column"
                            sx={{
                                width: "100%",
                                typography: "body1",
                                paddingTop: "26px",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                        >
                            <FormControl
                                label="Select repo / group"
                                sx={{ minWidth: 120 }}
                                size="small"
                            >
                                <InputLabel id="select-repo">Select repository</InputLabel>
                                <Select
                                    labelId="select-repo"
                                    id="select-repo"
                                    value={repo}
                                    label="Select Repository"
                                    onChange={handleChange}
                                >
                                    {repos.map((repository) => {
                                        return (
                                            <MenuItem key={repository.name} value={repository.name}>
                                                {repository.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            <Grid container spacing={2}>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={deploymentsSelected}
                                            onChange={handleCheckboxChange}
                                            value="deployments"
                                            name="set-deployments"
                                            size="small"
                                            inputProps={{ "aria-label": "deployments" }}
                                        />
                                        <TitleText>Deployments*</TitleText>
                                    </Stack>
                                    <Tooltip title="Use % as wildcard">
                                        <TextField
                                            id="outlined-basic"
                                            label="Environment"
                                            variant="outlined"
                                            size="small"
                                            autoComplete="off"
                                            disabled={!deploymentsSelected}
                                            value={
                                                !deploymentsSelected
                                                    ? ""
                                                    : (configurationDataPerRepo[repo] &&
                                                          configurationDataPerRepo[repo]
                                                              .deployments &&
                                                          configurationDataPerRepo[repo].deployments
                                                              .environment) ||
                                                      null
                                            }
                                            onChange={(event) => {
                                                setConfigurationDataPerRepo({
                                                    ...configurationDataPerRepo,
                                                    [repo]: {
                                                        ...configurationDataPerRepo[repo],
                                                        deployments: {
                                                            environment: event.target.value,
                                                        },
                                                    },
                                                });
                                            }}
                                            InputProps={{
                                                sx: {
                                                    fontSize: "12px",
                                                    fontWeight: 300,
                                                    padding: "3.5px",
                                                },
                                            }}
                                            InputLabelProps={{
                                                sx: {
                                                    fontSize: "12px",
                                                    fontWeight: 300,
                                                    padding: "3.5px",
                                                    "&.MuiOutlinedInput-notchedOutline": {
                                                        fontSize: "13px",
                                                    },
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={releasesSelected}
                                            onChange={handleCheckboxChange}
                                            value="releases"
                                            name="set-releases"
                                            size="small"
                                            inputProps={{ "aria-label": "releases" }}
                                        />
                                        <TitleText>Releases / Tags*</TitleText>
                                    </Stack>
                                    <Stack direction="column" spacing={2}>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Tag name"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!releasesSelected}
                                                value={
                                                    !releasesSelected
                                                        ? ""
                                                        : (configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .releases &&
                                                              configurationDataPerRepo[repo]
                                                                  .releases.name) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    setConfigurationDataPerRepo({
                                                        ...configurationDataPerRepo,
                                                        [repo]: {
                                                            ...configurationDataPerRepo[repo],
                                                            releases: {
                                                                name: event.target.value,
                                                            },
                                                        },
                                                    });
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={pipelinesSelected}
                                            onChange={handleCheckboxChange}
                                            value="pipelines"
                                            name="set-pipelines"
                                            size="small"
                                            inputProps={{ "aria-label": "pipelines" }}
                                        />
                                        <TitleText>Pipelines*</TitleText>
                                    </Stack>
                                    <Stack direction="column" spacing={2}>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Branch / Reference"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!pipelinesSelected}
                                                value={
                                                    !pipelinesSelected
                                                        ? ""
                                                        : (configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines.branch) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    setConfigurationDataPerRepo({
                                                        ...configurationDataPerRepo,
                                                        [repo]: {
                                                            ...configurationDataPerRepo[repo],
                                                            pipelines: {
                                                                branch: event.target.value,
                                                                workflow:
                                                                    (configurationDataPerRepo[repo]
                                                                        .pipelines &&
                                                                        configurationDataPerRepo[
                                                                            repo
                                                                        ].pipelines.workflow) ||
                                                                    undefined,
                                                            },
                                                        },
                                                    });
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Workflow"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!pipelinesSelected}
                                                value={
                                                    !pipelinesSelected
                                                        ? ""
                                                        : (configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines.workflow) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    setConfigurationDataPerRepo({
                                                        ...configurationDataPerRepo,
                                                        [repo]: {
                                                            ...configurationDataPerRepo[repo],
                                                            pipelines: {
                                                                branch:
                                                                    (configurationDataPerRepo[repo]
                                                                        .pipelines &&
                                                                        configurationDataPerRepo[
                                                                            repo
                                                                        ].pipelines.branch) ||
                                                                    undefined,
                                                                workflow: event.target.value,
                                                            },
                                                        },
                                                    });
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={webhookSelected}
                                            onChange={handleCheckboxChange}
                                            value="webhook"
                                            name="set-webhook"
                                            size="small"
                                            inputProps={{ "aria-label": "webhook" }}
                                        />
                                        <TitleText>Webhook</TitleText>
                                    </Stack>
                                    <p
                                        style={{
                                            fontSize: "11px",
                                            opacity: "0.5",
                                            marginBottom: 0,
                                        }}
                                    >
                                        No extra settings here.
                                    </p>
                                    <Link
                                        href="https://docs.adadot.com/Integrations/webhooks/"
                                        target="_blank"
                                        style={{ fontSize: "11px", opacity: "0.9" }}
                                    >
                                        How to configure webhooks
                                    </Link>
                                </Grid>
                            </Grid>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={2}
                                style={{ width: "100%", marginTop: "30px" }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={checkedTerms}
                                            onChange={handleTermsChange}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <p style={{ fontSize: "12px" }}>
                                            Change the configuration for the whole company.
                                        </p>
                                    }
                                    style={{ marginLeft: "-10px", marginRight: "6px" }}
                                />
                                <Stack direction="row" spacing={2}>
                                    <Button color="secondary" size="medium" onClick={handleClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        disabled={!checkedTerms}
                                        onClick={isDemo ? handleClose : handleSubmitConfiguration}
                                    >
                                        Save Configuration
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Modal>
        </div>
    );
};

export default OrgDeploymentsConfigModal;
